/**
 * Upload Image API
 *
 * Uploads an image to S3 via the backend API
 *
 * **Backend Reference:**
 * - Repository: `images-backend`
 * - Function: `image`
 * - Method: POST
 * - Endpoint: VITE_APP_IMAGE_UPLOAD_URL
 *
 * **Request Payload:**
 * ```json
 * {
 *   "image_data": "base64_encoded_string",
 *   "image_name": "merchantName_elementID",
 *   "image_type": "fundraiser" | "event" | "logo",
 *   "image_format": "jpeg" | "png",
 *   "nonprofitName": "organizationName"
 * }
 * ```
 *
 * **Response:**
 * - Success: Image uploaded to S3 bucket
 * - Returns: Image URL constructed from VITE_APP_IMAGE_URL
 *
 * @param {Object} params - Upload parameters
 * @param {string} params.imageData - Base64 encoded image data
 * @param {string} params.imageName - Image name (format: merchantName_elementID)
 * @param {string} params.imageType - Type of image ('fundraiser' | 'event' | 'logo')
 * @param {string} params.nonprofitName - Name of the nonprofit
 * @param {string} params.jwtToken - JWT authentication token
 *
 * @returns {Promise<{success: boolean, imageUrl?: string, error?: string}>}
 *
 * @example
 * const result = await uploadImage({
 *   imageData: 'data:image/png;base64,...',
 *   imageName: 'CharityOrg_abc123',
 *   imageType: 'fundraiser',
 *   nonprofitName: 'CharityOrg',
 *   jwtToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...'
 * });
 */

import { imagesUploadInputSchema } from '@charitystack/org-engagement-contracts';
import { optimizeLogoDataUrl } from './optimizeLogo';
import { selectImagesClient } from '../../src/lib/flags/images';
import { imageUrl } from '@charitystack/shared';

export interface UploadImageParams {
  organizationId?: unknown;
  imageData: string;
  imageName: string;
  imageType: 'fundraiser' | 'event' | 'logo';
  nonprofitName: string;
  jwtToken: string;
}

export interface UploadImageResponse {
  success: boolean;
  imageUrl?: string;
  imageVersion?: number;
  error?: string;
}

function imageFormatFromDataUrl(dataUrl: string): 'jpeg' | 'png' {
  const match = /^data:image\/(jpeg|png);base64,/i.exec(dataUrl);
  if (!match) {
    throw new Error('Image must be encoded as a JPEG or PNG data URL');
  }

  return match[1].toLowerCase() as 'jpeg' | 'png';
}

export const uploadImage = async (params: UploadImageParams): Promise<UploadImageResponse> => {
  const { organizationId, imageData, imageName, imageType, nonprofitName, jwtToken } = params;

  // Validate required parameters
  if (!imageData) {
    return {
      success: false,
      error: 'Image data is required',
    };
  }

  if (!imageName || !imageType || !nonprofitName) {
    return {
      success: false,
      error: 'Missing required parameters',
    };
  }

  try {
    const client = selectImagesClient({ organizationId, authorization: jwtToken });
    const normalizedImageData =
      imageType === 'logo' ? await optimizeLogoDataUrl(imageData) : imageData;
    const imageFormat = imageFormatFromDataUrl(normalizedImageData);
    const payload = imagesUploadInputSchema.parse({
      image_data: normalizedImageData,
      image_name: imageName,
      image_type: imageType,
      image_format: imageFormat,
      nonprofitName,
    });
    let responseData: { file_name?: string };
    if (client) {
      responseData = await client.uploadImage(payload);
    } else {
      const requestOptions = {
        method: 'POST',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: jwtToken,
          'x-cs-authorization': jwtToken,
        },
        body: JSON.stringify(payload),
      };
      const response = await fetch(import.meta.env.VITE_APP_IMAGE_UPLOAD_URL, requestOptions);
      const legacyResponseData = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: legacyResponseData.error || 'Failed to upload image',
        };
      }
      responseData = legacyResponseData;
    }

    // Use the key acknowledged by the backend so the display URL cannot drift from storage.
    const imageVersion = Date.now();
    const fileName = String(responseData.file_name || '');
    const dot = fileName.lastIndexOf('.');
    const path = dot >= 0 ? fileName.slice(0, dot) : fileName;
    const ext = dot >= 0 ? fileName.slice(dot + 1) : 'png';
    const slash = path.lastIndexOf('/');
    const imageTypeFromKey = slash >= 0 ? path.slice(0, slash) : imageType;
    const imageNameFromKey = slash >= 0 ? path.slice(slash + 1) : path;
    const uploadedImageUrl = imageUrl({
      base: import.meta.env.VITE_APP_IMAGE_URL,
      type: imageTypeFromKey,
      name: imageNameFromKey,
      ext,
      version: imageVersion,
    });

    return {
      success: true,
      imageUrl: uploadedImageUrl,
      imageVersion,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};
